import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Shield, ClipboardCheck, AlertTriangle, CheckCircle, XCircle,
  Clock, FileText, Download, Play, RefreshCw, TrendingUp,
  Calendar as CalendarIcon, Target, BarChart3, FileSearch,
  ChevronRight, AlertCircle, Gauge, ArrowUp, ArrowDown, Minus,
  Building2, Plane, Users, FolderOpen, Eye, User, FileCheck,
  Layers, List, History, Package, Timer, Wrench, Info,
  TrendingDown, Zap, Scale, Flag
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';

export default function AuditScenarioPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('run');
  const [auditTypes, setAuditTypes] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [trends, setTrends] = useState([]);
  const [inspectorProfiles, setInspectorProfiles] = useState({});
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [resultTab, setResultTab] = useState('overview');
  const [snapshots, setSnapshots] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    total_scenarios: 0,
    avg_score: 0,
    total_gaps: 0,
    critical_gaps: 0
  });
  
  // New scenario form
  const [scenarioForm, setScenarioForm] = useState({
    name: '',
    audit_type: '',
    scope: 'organization',
    scope_filter: '',
    scheduled_audit_date: null
  });

  // Fetch audit types
  useEffect(() => {
    const fetchAuditTypes = async () => {
      try {
        const response = await api.get('/audit-scenarios/config/audit-types');
        setAuditTypes(response.data.audit_types);
      } catch (error) {
        console.error('Failed to fetch audit types:', error);
      }
    };
    fetchAuditTypes();
  }, [api]);

  // Fetch scenarios
  const fetchScenarios = useCallback(async () => {
    try {
      const response = await api.get('/audit-scenarios/');
      const scenarioList = response.data.scenarios || [];
      setScenarios(scenarioList);
      
      // Calculate stats
      if (scenarioList.length > 0) {
        const avgScore = scenarioList.reduce((sum, s) => sum + (s.overall_score || 0), 0) / scenarioList.length;
        const totalGaps = scenarioList.reduce((sum, s) => sum + (s.total_gaps || 0), 0);
        const criticalGaps = scenarioList.reduce((sum, s) => sum + (s.critical_gaps || 0), 0);
        setStats({
          total_scenarios: scenarioList.length,
          avg_score: Math.round(avgScore),
          total_gaps: totalGaps,
          critical_gaps: criticalGaps
        });
      }
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
    }
  }, [api]);

  // Fetch trends
  const fetchTrends = useCallback(async () => {
    try {
      const response = await api.get('/audit-scenarios/history/trends?days=90');
      setTrends(response.data.trends || []);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  }, [api]);

  // Fetch snapshots for historical comparison
  const fetchSnapshots = useCallback(async () => {
    try {
      const response = await api.get('/audit-scenarios/history/snapshots?days=180&limit=100');
      setSnapshots(response.data.snapshots || []);
    } catch (error) {
      console.error('Failed to fetch snapshots:', error);
    }
  }, [api]);

  // Fetch inspector profile for selected audit type
  const fetchInspectorProfile = useCallback(async (auditType) => {
    if (!auditType || inspectorProfiles[auditType]) return;
    
    try {
      const response = await api.get(`/audit-scenarios/config/inspector-profile/${auditType}`);
      setInspectorProfiles(prev => ({ ...prev, [auditType]: response.data }));
    } catch (error) {
      console.error('Failed to fetch inspector profile:', error);
    }
  }, [api, inspectorProfiles]);

  useEffect(() => {
    fetchScenarios();
    fetchTrends();
    fetchSnapshots();
  }, [fetchScenarios, fetchTrends, fetchSnapshots]);

  // Fetch profile when audit type changes
  useEffect(() => {
    if (scenarioForm.audit_type) {
      fetchInspectorProfile(scenarioForm.audit_type);
    }
  }, [scenarioForm.audit_type, fetchInspectorProfile]);

  // Run new scenario
  const handleRunScenario = async () => {
    if (!scenarioForm.name || !scenarioForm.audit_type) {
      toast.error('Please provide scenario name and audit type');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        name: scenarioForm.name,
        audit_type: scenarioForm.audit_type,
        scope: scenarioForm.scope,
        scope_filter: scenarioForm.scope_filter || null,
        scheduled_audit_date: scenarioForm.scheduled_audit_date?.toISOString() || null
      };
      
      const response = await api.post('/audit-scenarios/run', payload);
      setCurrentScenario(response.data);
      setShowResultDialog(true);
      setResultTab('overview');
      fetchScenarios();
      fetchTrends();
      fetchSnapshots();
      toast.success('Audit scenario simulation completed');
    } catch (error) {
      console.error('Failed to run scenario:', error);
      toast.error('Failed to run audit scenario');
    }
    setLoading(false);
  };

  // View scenario details
  const handleViewScenario = async (scenarioId) => {
    try {
      const response = await api.get(`/audit-scenarios/${scenarioId}`);
      setCurrentScenario(response.data);
      setShowResultDialog(true);
      setResultTab('overview');
    } catch (error) {
      toast.error('Failed to load scenario');
    }
  };

  // Generate audit pack
  const handleGeneratePack = async (scenarioId) => {
    try {
      const response = await api.post(`/audit-scenarios/${scenarioId}/generate-pack`);
      toast.success('Audit pack generated successfully');
      // Download the pack data as JSON
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-pack-${scenarioId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate audit pack');
    }
  };

  // Export scenario
  const handleExportScenario = async (scenarioId, format = 'json') => {
    try {
      const response = await api.get(`/audit-scenarios/${scenarioId}/export?format=${format}`, {
        responseType: format === 'json' ? 'json' : 'blob'
      });
      
      const blob = format === 'json' 
        ? new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
        : new Blob([response.data], { type: 'text/csv' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-scenario-${scenarioId}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export scenario');
    }
  };

  // Quick readiness check
  const handleQuickCheck = async (auditType) => {
    setLoading(true);
    try {
      const response = await api.get(`/audit-scenarios/quick/readiness-score?audit_type=${auditType}`);
      toast.success(`Quick readiness: ${Math.round(response.data.overall_score)}%`);
    } catch (error) {
      toast.error('Failed to get quick readiness score');
    }
    setLoading(false);
  };

  // Download PDF report
  const handleDownloadReport = async (scenarioId) => {
    try {
      const response = await api.get(`/audit-scenarios/${scenarioId}/report-pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Audit_Report_${scenarioId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF report');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'compliant':
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Compliant</Badge>;
      case 'minor_gaps':
        return <Badge className="bg-amber-600"><AlertCircle className="w-3 h-3 mr-1" />Minor Gaps</Badge>;
      case 'major_gaps':
        return <Badge className="bg-orange-600"><AlertTriangle className="w-3 h-3 mr-1" />Major Gaps</Badge>;
      case 'non_compliant':
        return <Badge className="bg-red-600"><XCircle className="w-3 h-3 mr-1" />Non-Compliant</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get risk badge
  const getRiskBadge = (risk) => {
    const colors = {
      low: 'bg-emerald-600',
      medium: 'bg-amber-600',
      high: 'bg-orange-600',
      critical: 'bg-red-600'
    };
    return <Badge className={colors[risk] || 'bg-slate-600'}>{risk?.toUpperCase()}</Badge>;
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 70) return 'text-amber-400';
    if (score >= 50) return 'text-orange-400';
    return 'text-red-400';
  };

  // Get score bg color
  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-emerald-500';
    if (score >= 70) return 'bg-amber-500';
    if (score >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Get trend icon
  const getTrendIcon = (direction) => {
    if (direction === 'improving') return <ArrowUp className="w-4 h-4 text-emerald-400" />;
    if (direction === 'declining') return <ArrowDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  // Get feasibility color
  const getFeasibilityColor = (feasibility) => {
    switch (feasibility) {
      case 'fixable': return 'text-emerald-400 bg-emerald-900/30 border-emerald-700';
      case 'at_risk': return 'text-amber-400 bg-amber-900/30 border-amber-700';
      case 'not_achievable': return 'text-red-400 bg-red-900/30 border-red-700';
      default: return 'text-slate-400 bg-slate-900/30 border-slate-700';
    }
  };

  // Prepare chart data for trends
  const prepareChartData = () => {
    if (!snapshots.length) return [];
    
    // Group by date and calculate average
    const byDate = {};
    snapshots.forEach(s => {
      const date = s.snapshot_date?.split('T')[0];
      if (!byDate[date]) {
        byDate[date] = { scores: [], count: 0 };
      }
      byDate[date].scores.push(s.overall_score);
      byDate[date].count++;
    });
    
    return Object.entries(byDate)
      .map(([date, data]) => ({
        date: format(new Date(date), 'MMM dd'),
        score: Math.round(data.scores.reduce((a, b) => a + b, 0) / data.count)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-30);
  };

  const chartData = prepareChartData();

  // Get current profile
  const currentProfile = scenarioForm.audit_type ? inspectorProfiles[scenarioForm.audit_type] : null;

  return (
    <div className={cn("space-y-6 p-6", isDark ? "bg-slate-950" : "bg-gray-50")} data-testid="audit-scenario-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold flex items-center gap-3", isDark ? "text-white" : "text-gray-900")}>
            <Shield className="w-8 h-8 text-emerald-500" />
            Audit Scenario Simulation
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
            Pre-audit compliance assessment for GACA, EASA, FAA, and IOSA regulatory audits
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => fetchScenarios()}
            variant="outline"
            className={cn(isDark ? "border-slate-600" : "border-gray-300")}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setActiveTab('run')}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Play className="w-4 h-4 mr-2" />
            New Simulation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total Assessments</p>
                <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total_scenarios}</p>
              </div>
              <div className="p-3 bg-emerald-900/30 rounded-lg">
                <ClipboardCheck className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Average Score</p>
                <p className={cn("text-3xl font-bold", getScoreColor(stats.avg_score))}>{stats.avg_score}%</p>
              </div>
              <div className="p-3 bg-blue-900/30 rounded-lg">
                <Gauge className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total Gaps Found</p>
                <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total_gaps}</p>
              </div>
              <div className="p-3 bg-amber-900/30 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(stats.critical_gaps > 0 ? "border-red-500/50" : "", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Critical Gaps</p>
                <p className={cn("text-3xl font-bold", stats.critical_gaps > 0 ? "text-red-400" : "text-emerald-400")}>
                  {stats.critical_gaps}
                </p>
              </div>
              <div className={cn("p-3 rounded-lg", stats.critical_gaps > 0 ? "bg-red-900/30" : "bg-emerald-900/30")}>
                {stats.critical_gaps > 0 ? (
                  <XCircle className="w-6 h-6 text-red-400" />
                ) : (
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100 border-gray-200")}>
          <TabsTrigger value="run" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Play className="w-4 h-4 mr-2" />
            Run Simulation
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Compliance Trends
          </TabsTrigger>
          <TabsTrigger value="profiles" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <User className="w-4 h-4 mr-2" />
            Inspector Personas
          </TabsTrigger>
        </TabsList>

        {/* Run Simulation Tab */}
        <TabsContent value="run" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Configuration Card */}
            <Card className={cn("lg:col-span-2", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Target className="w-5 h-5 text-emerald-400" />
                  Configure Audit Simulation
                </CardTitle>
                <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                  Select audit type, scope, and optional audit date for countdown planning
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Step 1: Audit Type */}
                <div className="space-y-3">
                  <Label className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">1</span>
                    Select Audit Type
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {auditTypes.map(type => (
                      <div
                        key={type.id}
                        onClick={() => setScenarioForm(prev => ({ ...prev, audit_type: type.id }))}
                        className={cn(
                          "p-4 rounded-lg border-2 cursor-pointer transition-all",
                          scenarioForm.audit_type === type.id
                            ? "border-emerald-500 bg-emerald-900/30"
                            : isDark
                              ? "border-slate-700 bg-slate-900/50 hover:border-slate-600"
                              : "border-gray-200 bg-gray-50 hover:border-gray-300"
                        )}
                        data-testid={`audit-type-${type.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className={cn("w-5 h-5", scenarioForm.audit_type === type.id ? "text-emerald-400" : isDark ? "text-slate-400" : "text-gray-400")} />
                          <span className={cn("font-medium text-sm", isDark ? "text-white" : "text-gray-900")}>{type.name}</span>
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{type.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{type.clauses_count} clauses</Badge>
                          {type.documentation_language === 'bilingual' && (
                            <Badge className="bg-blue-600 text-xs">Bilingual</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Inspector Profile Preview */}
                {currentProfile && (
                  <div className={cn("p-4 rounded-lg border", isDark ? "bg-violet-900/20 border-violet-700" : "bg-violet-50 border-violet-200")}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-violet-400" />
                        <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                          Inspector Persona: {currentProfile.name}
                        </span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setSelectedProfile(currentProfile);
                          setShowProfileDialog(true);
                        }}
                        className="text-violet-400 hover:text-violet-300"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                    <p className={cn("text-sm mb-3", isDark ? "text-slate-400" : "text-gray-600")}>{currentProfile.description}</p>
                    <div className="flex flex-wrap gap-2">
                      <span className={cn("text-xs font-medium", isDark ? "text-slate-500" : "text-gray-400")}>Focus Areas:</span>
                      {currentProfile.focus_areas?.slice(0, 4).map((focus, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs border-violet-600 text-violet-400">
                          {focus.category?.replace('_', ' ')} ({focus.weight_multiplier}x)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Scope */}
                <div className="space-y-3">
                  <Label className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">2</span>
                    Select Scope
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { id: 'organization', icon: Building2, label: 'Organization', desc: 'Full org assessment' },
                      { id: 'fleet', icon: Plane, label: 'Fleet', desc: 'Aircraft fleet specific' },
                      { id: 'base', icon: FolderOpen, label: 'Base', desc: 'Station/base specific' },
                      { id: 'department', icon: Users, label: 'Department', desc: 'Department specific' }
                    ].map(scope => (
                      <div
                        key={scope.id}
                        onClick={() => setScenarioForm(prev => ({ ...prev, scope: scope.id }))}
                        className={cn(
                          "p-3 rounded-lg border-2 cursor-pointer transition-all text-center",
                          scenarioForm.scope === scope.id
                            ? "border-emerald-500 bg-emerald-900/30"
                            : isDark
                              ? "border-slate-700 bg-slate-900/50 hover:border-slate-600"
                              : "border-gray-200 bg-gray-50 hover:border-gray-300"
                        )}
                        data-testid={`scope-${scope.id}`}
                      >
                        <scope.icon className={cn("w-6 h-6 mx-auto mb-1", scenarioForm.scope === scope.id ? "text-emerald-400" : isDark ? "text-slate-400" : "text-gray-400")} />
                        <span className={cn("text-sm block", scenarioForm.scope === scope.id ? "text-white font-medium" : isDark ? "text-slate-300" : "text-gray-700")}>{scope.label}</span>
                        <span className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>{scope.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Step 3: Details */}
                <div className="space-y-3">
                  <Label className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">3</span>
                    Scenario Details
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={isDark ? "text-slate-300" : "text-gray-700"}>Scenario Name *</Label>
                      <Select
                        value={scenarioForm.name}
                        onValueChange={(value) => setScenarioForm(prev => ({ ...prev, name: value }))}
                      >
                        <SelectTrigger 
                          className={cn(isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}
                          data-testid="scenario-name-select"
                        >
                          <SelectValue placeholder="Select scenario type" />
                        </SelectTrigger>
                        <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200")}>
                          <SelectItem value="Pre-Audit Readiness Assessment">Pre-Audit Readiness Assessment</SelectItem>
                          <SelectItem value="Annual Compliance Review">Annual Compliance Review</SelectItem>
                          <SelectItem value="Quarterly SMS Health Check">Quarterly SMS Health Check</SelectItem>
                          <SelectItem value="Pre-Inspection Preparation">Pre-Inspection Preparation</SelectItem>
                          <SelectItem value="Gap Analysis Assessment">Gap Analysis Assessment</SelectItem>
                          <SelectItem value="Regulatory Audit Simulation">Regulatory Audit Simulation</SelectItem>
                          <SelectItem value="Internal Audit Dry Run">Internal Audit Dry Run</SelectItem>
                          <SelectItem value="Certification Readiness Check">Certification Readiness Check</SelectItem>
                          <SelectItem value="Surveillance Audit Preparation">Surveillance Audit Preparation</SelectItem>
                          <SelectItem value="Management Review Assessment">Management Review Assessment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className={isDark ? "text-slate-300" : "text-gray-700"}>Scheduled Audit Date (Optional)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className={cn("w-full justify-start", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}
                            data-testid="audit-date-picker"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {scenarioForm.scheduled_audit_date 
                              ? format(scenarioForm.scheduled_audit_date, 'PPP')
                              : 'Select date for countdown planning'
                            }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className={cn("w-auto p-0", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200")}>
                          <Calendar
                            mode="single"
                            selected={scenarioForm.scheduled_audit_date}
                            onSelect={(date) => setScenarioForm(prev => ({ ...prev, scheduled_audit_date: date }))}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {scenarioForm.scheduled_audit_date && (
                        <p className="text-xs text-emerald-400">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {Math.ceil((scenarioForm.scheduled_audit_date - new Date()) / (1000 * 60 * 60 * 24))} days until audit
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Run Button */}
                <Button 
                  onClick={handleRunScenario}
                  disabled={loading || !scenarioForm.name || !scenarioForm.audit_type}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-lg"
                  data-testid="run-simulation-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Run Compliance Assessment
                </Button>
              </CardContent>
            </Card>

            {/* Info Cards */}
            <div className="space-y-4">
              {/* What Gets Checked */}
              <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                <CardHeader className="pb-3">
                  <CardTitle className={cn("flex items-center gap-2 text-base", isDark ? "text-white" : "text-gray-900")}>
                    <FileSearch className="w-5 h-5 text-blue-400" />
                    What Gets Checked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    {[
                      { icon: Shield, label: 'Safety Policy & Accountability', color: 'text-emerald-400' },
                      { icon: AlertTriangle, label: 'Hazard Reporting System', color: 'text-orange-400' },
                      { icon: Target, label: 'Risk Register & Mitigations', color: 'text-amber-400' },
                      { icon: BarChart3, label: 'SPIs & Safety Assurance', color: 'text-blue-400' },
                      { icon: ClipboardCheck, label: 'Audits & CAPA Tracking', color: 'text-purple-400' },
                      { icon: Users, label: 'Training Currency', color: 'text-cyan-400' },
                      { icon: FileText, label: 'Evidence Documentation', color: 'text-pink-400' }
                    ].map((item, idx) => (
                      <div key={idx} className={cn("flex items-center gap-3 p-2 rounded", isDark ? "bg-slate-900/50" : "bg-gray-50")}>
                        <item.icon className={cn("w-4 h-4", item.color)} />
                        <span className={isDark ? "text-slate-300" : "text-gray-700"}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Readiness Check */}
              <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                <CardHeader className="pb-3">
                  <CardTitle className={cn("flex items-center gap-2 text-base", isDark ? "text-white" : "text-gray-900")}>
                    <Zap className="w-5 h-5 text-amber-400" />
                    Quick Readiness Check
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={cn("text-xs mb-3", isDark ? "text-slate-400" : "text-gray-500")}>
                    Get instant readiness score without saving
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {auditTypes.slice(0, 4).map(type => (
                      <Button
                        key={type.id}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickCheck(type.id)}
                        disabled={loading}
                        className={cn("text-xs", isDark ? "border-slate-600" : "border-gray-300")}
                      >
                        {type.name.split(' ')[0]}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <History className="w-5 h-5 text-emerald-400" />
                Assessment History
              </CardTitle>
              <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                View and compare previous audit simulations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {scenarios.map(scenario => (
                    <div 
                      key={scenario.id}
                      className={cn(
                        "p-4 rounded-lg border cursor-pointer transition-all",
                        isDark 
                          ? "bg-slate-900/50 border-slate-700 hover:border-slate-600" 
                          : "bg-gray-50 border-gray-200 hover:border-gray-300"
                      )}
                      onClick={() => handleViewScenario(scenario.id)}
                      data-testid={`scenario-${scenario.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{scenario.name}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">{scenario.audit_type?.replace('_', ' ').toUpperCase()}</Badge>
                            {getStatusBadge(scenario.overall_status)}
                            {getRiskBadge(scenario.overall_risk)}
                          </div>
                          <p className={cn("text-xs mt-2", isDark ? "text-slate-400" : "text-gray-500")}>
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(scenario.created_at).toLocaleDateString()} • {scenario.total_gaps} gaps found
                          </p>
                        </div>
                        <div className="text-right ml-4">
                          <div className={cn("text-3xl font-bold", getScoreColor(scenario.overall_score))}>
                            {Math.round(scenario.overall_score)}%
                          </div>
                          <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Compliance Score</p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-700/50 flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={(e) => { e.stopPropagation(); handleGeneratePack(scenario.id); }}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Audit Pack
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={(e) => { e.stopPropagation(); handleExportScenario(scenario.id, 'csv'); }}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Export CSV
                        </Button>
                      </div>
                    </div>
                  ))}
                  {scenarios.length === 0 && (
                    <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                      <ClipboardCheck className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No assessments yet</p>
                      <p className="text-sm mt-1">Run your first simulation to see history here</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          {/* Historical Score Chart */}
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Historical Compliance Scores
              </CardTitle>
              <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                Compliance score trends over the past 90 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                      <XAxis dataKey="date" stroke={isDark ? "#64748b" : "#9ca3af"} fontSize={12} />
                      <YAxis stroke={isDark ? "#64748b" : "#9ca3af"} fontSize={12} domain={[0, 100]} />
                      <RechartsTooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1e293b' : '#fff', 
                          border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: isDark ? '#fff' : '#111' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#10b981" 
                        fillOpacity={1}
                        fill="url(#colorScore)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Run multiple assessments to see trend data</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Trends */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trends.map((trend, idx) => (
              <Card key={idx} className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={cn("font-medium capitalize", isDark ? "text-white" : "text-gray-900")}>
                      {trend.category?.replace('_', ' ')}
                    </h3>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(trend.trend_direction)}
                      <span className={cn(
                        "text-sm font-medium",
                        trend.trend_direction === 'improving' ? 'text-emerald-400' :
                        trend.trend_direction === 'declining' ? 'text-red-400' : 'text-slate-400'
                      )}>
                        {trend.change_percent > 0 ? '+' : ''}{trend.change_percent}%
                      </span>
                    </div>
                  </div>
                  {trend.data_points?.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className={isDark ? "text-slate-400" : "text-gray-500"}>Latest Score</span>
                        <span className={getScoreColor(trend.data_points[trend.data_points.length - 1]?.score)}>
                          {Math.round(trend.data_points[trend.data_points.length - 1]?.score || 0)}%
                        </span>
                      </div>
                      <Progress 
                        value={trend.data_points[trend.data_points.length - 1]?.score || 0}
                        className="h-2"
                      />
                      <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>
                        {trend.data_points.length} data points over 90 days
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {trends.length === 0 && (
              <Card className={cn("col-span-full", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                <CardContent className={cn("py-12 text-center", isDark ? "text-slate-500" : "text-gray-400")}>
                  <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Run multiple assessments to see compliance trends over time</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Inspector Profiles Tab */}
        <TabsContent value="profiles" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <User className="w-5 h-5 text-violet-400" />
                Inspector Persona Profiles
              </CardTitle>
              <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                Understand how different regulatory inspectors weigh compliance areas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {auditTypes.map(type => {
                  const profile = inspectorProfiles[type.id];
                  
                  return (
                    <div 
                      key={type.id}
                      className={cn(
                        "p-4 rounded-lg border transition-all cursor-pointer",
                        isDark 
                          ? "bg-slate-900/50 border-slate-700 hover:border-violet-600" 
                          : "bg-gray-50 border-gray-200 hover:border-violet-400"
                      )}
                      onClick={() => {
                        if (!profile) fetchInspectorProfile(type.id);
                        setSelectedProfile(profile || null);
                        setShowProfileDialog(true);
                      }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-violet-900/30 rounded-lg">
                          <User className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                          <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{type.name}</h3>
                          <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>{type.clauses_count} clauses</p>
                        </div>
                      </div>
                      
                      {profile ? (
                        <div className="space-y-3">
                          <div>
                            <p className={cn("text-xs font-medium mb-1", isDark ? "text-slate-400" : "text-gray-500")}>Focus Areas:</p>
                            <div className="flex flex-wrap gap-1">
                              {profile.focus_areas?.map((focus, idx) => (
                                <Badge 
                                  key={idx} 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    focus.weight_multiplier >= 1.3 ? "border-red-500 text-red-400" :
                                    focus.weight_multiplier >= 1.2 ? "border-amber-500 text-amber-400" :
                                    "border-slate-500 text-slate-400"
                                  )}
                                >
                                  {focus.category?.replace('_', ' ')} ({focus.weight_multiplier}x)
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className={cn("text-xs font-medium mb-1", isDark ? "text-slate-400" : "text-gray-500")}>Common Findings:</p>
                            <ul className={cn("text-xs space-y-1", isDark ? "text-slate-400" : "text-gray-600")}>
                              {profile.common_findings?.slice(0, 2).map((finding, idx) => (
                                <li key={idx} className="flex items-start gap-1">
                                  <AlertCircle className="w-3 h-3 mt-0.5 text-amber-400 flex-shrink-0" />
                                  <span className="line-clamp-1">{finding}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full text-violet-400"
                          onClick={(e) => { e.stopPropagation(); fetchInspectorProfile(type.id); }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Load Profile
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Results Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className={cn("max-w-5xl max-h-[90vh] overflow-hidden", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
              <Gauge className="w-6 h-6 text-emerald-400" />
              Compliance Assessment Results
            </DialogTitle>
            <DialogDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
              {currentScenario?.name} • {currentScenario?.audit_type?.replace('_', ' ').toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          
          {currentScenario && (
            <Tabs value={resultTab} onValueChange={setResultTab} className="flex-1">
              <TabsList className={cn("mb-4", isDark ? "bg-slate-800" : "bg-gray-100")}>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="gaps">
                  Gap Analysis
                  {currentScenario.total_gaps > 0 && (
                    <Badge className="ml-2 bg-amber-600 text-xs">{currentScenario.total_gaps}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="evidence">Evidence Matrix</TabsTrigger>
                {currentScenario.countdown_plan && (
                  <TabsTrigger value="action">Action Plan</TabsTrigger>
                )}
              </TabsList>

              <ScrollArea className="h-[60vh]">
                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 m-0">
                  {/* Score Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className={cn("col-span-2", isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                      <CardContent className="pt-6 text-center">
                        <div className={cn("text-6xl font-bold", getScoreColor(currentScenario.overall_score))}>
                          {Math.round(currentScenario.overall_score)}%
                        </div>
                        <p className={cn("mt-2", isDark ? "text-slate-400" : "text-gray-500")}>Overall Compliance Score</p>
                        <div className="flex justify-center gap-2 mt-4">
                          {getStatusBadge(currentScenario.overall_status)}
                          {getRiskBadge(currentScenario.overall_risk)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>{currentScenario.total_gaps}</div>
                          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Total Gaps</p>
                        </div>
                        <div className="mt-4 space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-red-400">Critical</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.critical_gaps}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-orange-400">Major</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.major_gaps}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-400">Minor</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.minor_gaps}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                            {currentScenario.evidence_complete + currentScenario.evidence_missing}
                          </div>
                          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Evidence Items</p>
                        </div>
                        <div className="mt-4 space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-emerald-400">Linked</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.evidence_complete}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-red-400">Missing</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.evidence_missing}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-400">Expired</span>
                            <span className={isDark ? "text-white" : "text-gray-900"}>{currentScenario.evidence_expired}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Compliance Areas */}
                  <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                    <CardHeader>
                      <CardTitle className={cn("text-lg", isDark ? "text-white" : "text-gray-900")}>Compliance Areas Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {currentScenario.compliance_areas?.map((area, idx) => (
                          <div key={idx} className={cn("p-3 rounded border", isDark ? "bg-slate-900/50 border-slate-700" : "bg-white border-gray-200")}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{area.name}</span>
                                {getStatusBadge(area.status)}
                              </div>
                              <span className={cn("text-xl font-bold", getScoreColor(area.score))}>
                                {Math.round(area.score)}%
                              </span>
                            </div>
                            <Progress value={area.score} className="h-2" />
                            <div className={cn("flex gap-4 mt-2 text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                              <span>{area.clauses_compliant}/{area.clauses_total} compliant</span>
                              {area.clauses_minor_gap > 0 && <span className="text-amber-400">{area.clauses_minor_gap} minor</span>}
                              {area.clauses_major_gap > 0 && <span className="text-red-400">{area.clauses_major_gap} major</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Gap Analysis Tab */}
                <TabsContent value="gaps" className="space-y-4 m-0">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card className={cn("border-red-500/50", isDark ? "bg-red-900/20" : "bg-red-50")}>
                      <CardContent className="pt-4 text-center">
                        <XCircle className="w-8 h-8 mx-auto text-red-400 mb-2" />
                        <div className="text-2xl font-bold text-red-400">{currentScenario.critical_gaps}</div>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Critical Gaps</p>
                      </CardContent>
                    </Card>
                    <Card className={cn("border-orange-500/50", isDark ? "bg-orange-900/20" : "bg-orange-50")}>
                      <CardContent className="pt-4 text-center">
                        <AlertTriangle className="w-8 h-8 mx-auto text-orange-400 mb-2" />
                        <div className="text-2xl font-bold text-orange-400">{currentScenario.major_gaps}</div>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Major Gaps</p>
                      </CardContent>
                    </Card>
                    <Card className={cn("border-amber-500/50", isDark ? "bg-amber-900/20" : "bg-amber-50")}>
                      <CardContent className="pt-4 text-center">
                        <AlertCircle className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                        <div className="text-2xl font-bold text-amber-400">{currentScenario.minor_gaps}</div>
                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>Minor Gaps</p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                    <CardHeader>
                      <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                        <List className="w-5 h-5 text-amber-400" />
                        Gap Details with Time-to-Fix Estimates
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {currentScenario.gaps?.map((gap, idx) => (
                          <div 
                            key={idx} 
                            className={cn(
                              "p-3 rounded border",
                              gap.severity === 'critical' ? (isDark ? "bg-red-900/20 border-red-700" : "bg-red-50 border-red-200") :
                              gap.severity === 'major' ? (isDark ? "bg-orange-900/20 border-orange-700" : "bg-orange-50 border-orange-200") :
                              (isDark ? "bg-amber-900/20 border-amber-700" : "bg-amber-50 border-amber-200")
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={
                                    gap.severity === 'critical' ? 'bg-red-600' :
                                    gap.severity === 'major' ? 'bg-orange-600' : 'bg-amber-600'
                                  }>
                                    {gap.severity?.toUpperCase()}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">{gap.clause_code}</Badge>
                                  <Badge variant="outline" className="text-xs capitalize">{gap.category?.replace('_', ' ')}</Badge>
                                </div>
                                <h4 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{gap.clause_title}</h4>
                                <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-600")}>{gap.description}</p>
                                {gap.missing_documents?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    <span className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Missing:</span>
                                    {gap.missing_documents.map((doc, i) => (
                                      <Badge key={i} variant="outline" className="text-xs text-red-400 border-red-600">
                                        {doc}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-right ml-4">
                                <div className={cn("flex items-center gap-1 px-3 py-1 rounded", isDark ? "bg-slate-800" : "bg-white")}>
                                  <Timer className="w-4 h-4 text-blue-400" />
                                  <span className={cn("font-bold", isDark ? "text-white" : "text-gray-900")}>{gap.estimated_fix_days}</span>
                                  <span className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>days</span>
                                </div>
                                <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-400")}>Est. to fix</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {currentScenario.gaps?.length === 0 && (
                          <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                            <CheckCircle className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                            <p>No compliance gaps found!</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Evidence Matrix Tab */}
                <TabsContent value="evidence" className="space-y-4 m-0">
                  <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                    <CardHeader>
                      <CardTitle className={cn("text-lg flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                        <FileCheck className="w-5 h-5 text-blue-400" />
                        Evidence-to-Clause Mapping Matrix
                      </CardTitle>
                      <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                        Document evidence status for each regulatory clause
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {currentScenario.evidence_links?.map((evidence, idx) => (
                          <div 
                            key={idx} 
                            className={cn(
                              "flex items-center justify-between p-3 rounded border",
                              evidence.status === 'linked' ? (isDark ? "bg-emerald-900/20 border-emerald-700" : "bg-emerald-50 border-emerald-200") :
                              evidence.status === 'expired' ? (isDark ? "bg-amber-900/20 border-amber-700" : "bg-amber-50 border-amber-200") :
                              (isDark ? "bg-red-900/20 border-red-700" : "bg-red-50 border-red-200")
                            )}
                          >
                            <div className="flex items-center gap-3">
                              {evidence.status === 'linked' ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                              ) : evidence.status === 'expired' ? (
                                <Clock className="w-5 h-5 text-amber-400" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">{evidence.clause_code}</Badge>
                                  <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                                    {evidence.document_type?.replace('_', ' ')}
                                  </span>
                                </div>
                                {evidence.document_title && (
                                  <p className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                                    {evidence.document_number} - {evidence.document_title}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className={
                                evidence.status === 'linked' ? 'bg-emerald-600' :
                                evidence.status === 'expired' ? 'bg-amber-600' : 'bg-red-600'
                              }>
                                {evidence.status?.toUpperCase()}
                              </Badge>
                              {evidence.expiry_date && (
                                <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-400")}>
                                  Expires: {new Date(evidence.expiry_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        {currentScenario.evidence_links?.length === 0 && (
                          <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No evidence requirements found</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Action Plan Tab */}
                {currentScenario.countdown_plan && (
                  <TabsContent value="action" className="space-y-4 m-0">
                    <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-gray-50 border-gray-200")}>
                      <CardHeader>
                        <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                          <Clock className="w-5 h-5 text-amber-400" />
                          Pre-Audit Action Plan
                        </CardTitle>
                        <CardDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
                          <span className="text-2xl font-bold text-amber-400">{currentScenario.countdown_plan.days_remaining}</span> days until audit
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className={cn("text-center p-4 rounded", isDark ? "bg-emerald-900/30" : "bg-emerald-50")}>
                            <div className="text-3xl font-bold text-emerald-400">{currentScenario.countdown_plan.fixable_count}</div>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Fixable</p>
                            <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Can be resolved before audit</p>
                          </div>
                          <div className={cn("text-center p-4 rounded", isDark ? "bg-amber-900/30" : "bg-amber-50")}>
                            <div className="text-3xl font-bold text-amber-400">{currentScenario.countdown_plan.at_risk_count}</div>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>At Risk</p>
                            <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Requires fast-track resolution</p>
                          </div>
                          <div className={cn("text-center p-4 rounded", isDark ? "bg-red-900/30" : "bg-red-50")}>
                            <div className="text-3xl font-bold text-red-400">{currentScenario.countdown_plan.not_achievable_count}</div>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Not Achievable</p>
                            <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Prepare mitigation strategy</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className={cn("font-medium mb-3", isDark ? "text-white" : "text-gray-900")}>Prioritized Action Items</h4>
                          {currentScenario.countdown_plan.action_items?.slice(0, 15).map((item, idx) => (
                            <div 
                              key={idx} 
                              className={cn(
                                "flex items-center justify-between p-3 rounded border",
                                getFeasibilityColor(item.feasibility)
                              )}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={
                                    item.severity === 'critical' ? 'bg-red-600' :
                                    item.severity === 'major' ? 'bg-orange-600' : 'bg-amber-600'
                                  }>
                                    {item.severity}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs capitalize">{item.category?.replace('_', ' ')}</Badge>
                                </div>
                                <p className={cn("text-sm", isDark ? "text-white" : "text-gray-900")}>{item.gap_description}</p>
                                <p className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-gray-500")}>{item.recommended_action}</p>
                              </div>
                              <div className="text-right ml-4">
                                <div className="flex items-center gap-1">
                                  <Timer className="w-4 h-4" />
                                  <span className="font-bold">{item.estimated_days}d</span>
                                </div>
                                <Badge className={
                                  item.feasibility === 'fixable' ? 'bg-emerald-600' :
                                  item.feasibility === 'at_risk' ? 'bg-amber-600' : 'bg-red-600'
                                }>
                                  {item.feasibility?.replace('_', ' ')}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}
              </ScrollArea>
            </Tabs>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Close
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleExportScenario(currentScenario?.id, 'csv')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleDownloadReport(currentScenario?.id)}
              className="border-blue-500 text-blue-400 hover:bg-blue-900/20"
            >
              <FileText className="w-4 h-4 mr-2" />
              Download PDF Report
            </Button>
            <Button onClick={() => handleGeneratePack(currentScenario?.id)} className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" />
              Generate Audit Pack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspector Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
              <User className="w-6 h-6 text-violet-400" />
              Inspector Persona Profile
            </DialogTitle>
          </DialogHeader>
          
          {selectedProfile ? (
            <div className="space-y-6">
              <div>
                <h3 className={cn("text-lg font-semibold", isDark ? "text-white" : "text-gray-900")}>{selectedProfile.name}</h3>
                <p className={cn("mt-1", isDark ? "text-slate-400" : "text-gray-500")}>{selectedProfile.description}</p>
                <Badge className="mt-2 bg-violet-600">{selectedProfile.documentation_language}</Badge>
              </div>

              <div>
                <h4 className={cn("font-medium mb-3 flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Target className="w-4 h-4 text-violet-400" />
                  Focus Areas & Weighting
                </h4>
                <div className="space-y-2">
                  {selectedProfile.focus_areas?.map((focus, idx) => (
                    <div key={idx} className={cn("p-3 rounded border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn("font-medium capitalize", isDark ? "text-white" : "text-gray-900")}>
                          {focus.category?.replace('_', ' ')}
                        </span>
                        <Badge className={
                          focus.weight_multiplier >= 1.4 ? 'bg-red-600' :
                          focus.weight_multiplier >= 1.3 ? 'bg-orange-600' :
                          focus.weight_multiplier >= 1.2 ? 'bg-amber-600' : 'bg-blue-600'
                        }>
                          {focus.weight_multiplier}x weight
                        </Badge>
                      </div>
                      <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>{focus.notes}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className={cn("font-medium mb-3 flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                  Common Findings to Watch
                </h4>
                <ul className="space-y-2">
                  {selectedProfile.common_findings?.map((finding, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Flag className="w-4 h-4 mt-0.5 text-amber-400 flex-shrink-0" />
                      <span className={cn("text-sm", isDark ? "text-slate-300" : "text-gray-700")}>{finding}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
              <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
              <p>Loading profile...</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
